import { MutationCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from '@lombok/api-client';
import { notifyMutationError, notifyMutationSuccess } from '@/lib/mutationToasts';

/**
 * Shared TanStack Query client. All server-state flows through Query — no
 * ad-hoc fetch in components. Auth failures are not retried (the user must
 * re-authenticate); transient errors get a couple of retries.
 */
export const queryClient = new QueryClient({
  // Every mutation error shows a toast unless the mutation opts out with
  // `meta: { inlineError: true }` (forms that render FormError themselves).
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => notifyMutationError(error, mutation.meta),
    onSuccess: (_data, _variables, _context, mutation) => notifyMutationSuccess(mutation.meta),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && (error.isUnauthorized || error.isForbidden)) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/** Stable, namespaced query keys so cache invalidation stays predictable. */
export const queryKeys = {
  me: ['me'] as const,
  products: (params?: Record<string, unknown>) => ['products', params ?? {}] as const,
  brands: ['brands'] as const,
  categories: ['categories'] as const,
  product: (id: string) => ['products', 'detail', id] as const,
  customers: (params?: Record<string, unknown>) => ['customers', params ?? {}] as const,
  customer: (id: string) => ['customers', 'detail', id] as const,
  distributors: (params?: Record<string, unknown>) => ['distributors', params ?? {}] as const,
  distributor: (id: string) => ['distributors', 'detail', id] as const,
  inventory: (params?: Record<string, unknown>) => ['inventory', params ?? {}] as const,
  orders: (params?: Record<string, unknown>) => ['orders', params ?? {}] as const,
  order: (id: string) => ['orders', 'detail', id] as const,
  quotes: (params?: Record<string, unknown>) => ['quotes', params ?? {}] as const,
  quote: (id: string) => ['quotes', 'detail', id] as const,
  skus: (productId: string) => ['products', 'detail', productId, 'skus'] as const,
  payments: (params?: Record<string, unknown>) => ['payments', params ?? {}] as const,
  payment: (id: string, accountId = '') => ['payments', 'detail', id, accountId] as const,
  invoices: (params?: Record<string, unknown>) => ['invoices', params ?? {}] as const,
  invoice: (id: string, customerId = '') => ['invoices', 'detail', id, customerId] as const,
  notifications: (params?: Record<string, unknown>) => ['notifications', params ?? {}] as const,
  reportsDashboard: ['reports', 'dashboard'] as const,
  arAging: ['reports', 'ar-aging'] as const,
  exchangeRate: ['settings', 'exchange-rate'] as const,
  exchangeRateHistory: ['settings', 'exchange-rate', 'history'] as const,
  documents: (accountId: string) => ['documents', accountId] as const,
  documentReviewQueue: (status: string) => ['documents', 'review-queue', status] as const,
  promotions: ['promotions'] as const,
  promotion: (id: string) => ['promotions', 'detail', id] as const,
  health: ['health'] as const,
};
